import { useEffect, useState } from "react";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, Empty } from "@/components/ui-bits";
import { fmtINR, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Handshake, FilePdf, ArrowRight, CurrencyInr } from "@phosphor-icons/react";

export default function Settlements() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [active, setActive] = useState(null);

  const load = () => {
    const params = status ? { status } : {};
    api.get("/settlements", { params }).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [status]);

  return (
    <div data-testid="settlements-page">
      <PageHeader title="Settlements" subtitle="Multi-stage approval workflow">
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44 rounded-sm" data-testid="settlement-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {["pending_manager", "pending_admin", "approved", "rejected", "countered"].map((s) => (
              <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PageHeader>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="label-caps border-b border-border bg-secondary/40">
              <th className="text-left p-3">Loan ID</th>
              <th className="text-left p-3">Borrower</th>
              <th className="text-right p-3">Outstanding</th>
              <th className="text-right p-3">Settlement</th>
              <th className="text-right p-3">Waiver</th>
              <th className="text-left p-3">Raised By</th>
              <th className="text-center p-3">Status</th>
              <th className="text-center p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-border last:border-0 hover:bg-secondary/50 cursor-pointer" onClick={() => setActive(s.id)} data-testid={`settlement-row-${s.id}`}>
                <td className="p-3 mono text-primary font-medium">{s.loan_id}</td>
                <td className="p-3 font-medium">{s.borrower_name}</td>
                <td className="p-3 text-right mono">{fmtINR(s.outstanding_amount)}</td>
                <td className="p-3 text-right mono font-semibold">
                  <span className="inline-flex items-center gap-1 justify-end">
                    <CurrencyInr size={13} weight="bold" className="text-primary" />
                    {fmtINR(s.settlement_amount)}
                  </span>
                </td>
                <td className="p-3 text-right mono text-emerald-600">
                  {fmtINR(s.outstanding_amount - s.settlement_amount)}
                  <span className="text-xs text-muted-foreground ml-1">
                    ({s.outstanding_amount ? Math.round((1 - s.settlement_amount / s.outstanding_amount) * 100) : 0}%)
                  </span>
                </td>
                <td className="p-3 text-muted-foreground">{s.created_by_name}</td>
                <td className="p-3 text-center"><StatusBadge status={s.status} /></td>
                <td className="p-3 text-center"><ArrowRight size={16} className="text-muted-foreground inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <Empty text="No settlement requests" />}
      </div>

      {active && <SettlementDialog id={active} user={user} onClose={() => setActive(null)} onDone={load} />}
    </div>
  );
}

function SettlementDialog({ id, user, onClose, onDone }) {
  const [s, setS] = useState(null);
  const [counter, setCounter] = useState("");
  const [comment, setComment] = useState("");

  const load = () => api.get(`/settlements/${id}`).then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const act = async (action) => {
    try {
      await api.post(`/settlements/${id}/action`, { action, comment, counter_amount: action === "counter" ? +counter : undefined });
      toast.success(`Settlement ${action}`);
      load(); onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (!s) return null;
  const canAct = ["manager", "client_admin", "super_admin"].includes(user.role) &&
    ["pending_manager", "pending_admin"].includes(s.status);
  const waiver = Math.max(0, s.outstanding_amount - s.settlement_amount);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Handshake size={20} /> Settlement • {s.loan_id}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Info label="Borrower" value={s.borrower_name} />
          <Info label="Status" value={<StatusBadge status={s.status} />} />
          <Info label="Outstanding" value={fmtINR(s.outstanding_amount)} />
          <Info label="Settlement" value={fmtINR(s.settlement_amount)} />
          <Info label="Waiver" value={fmtINR(waiver)} />
          <Info label="Stage" value={<span className="capitalize">{s.current_stage}</span>} />
        </div>
        <div>
          <div className="label-caps mb-1">Reason</div>
          <p className="text-sm text-muted-foreground">{s.reason || "—"} {s.notes ? `• ${s.notes}` : ""}</p>
        </div>

        <div className="border-t border-border pt-3">
          <div className="label-caps mb-2">Action History</div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {s.actions?.map((a) => (
              <div key={a.id} className="flex items-start justify-between text-sm">
                <div>
                  <span className="font-semibold capitalize">{a.action}</span>
                  <span className="text-muted-foreground"> by {a.actor_name} ({a.stage})</span>
                  {a.comment && <div className="text-xs text-muted-foreground">{a.comment}</div>}
                  {a.counter_amount && <div className="text-xs mono">Counter: {fmtINR(a.counter_amount)}</div>}
                </div>
                <span className="text-xs text-muted-foreground mono shrink-0">{fmtDateTime(a.created_at)}</span>
              </div>
            ))}
          </div>
        </div>

        {canAct && (
          <div className="border-t border-border pt-3 space-y-2">
            <Textarea placeholder="Comment (optional)" className="rounded-sm" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="settlement-comment" />
            <div className="flex items-center gap-2">
              <Input type="number" placeholder="Counter amount" className="rounded-sm w-40" value={counter} onChange={(e) => setCounter(e.target.value)} />
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => act("counter")} data-testid="settlement-counter">Counter</Button>
            </div>
          </div>
        )}

        <div className="border-t border-border pt-3">
          <div className="label-caps mb-2">Generate Documents</div>
          <div className="grid grid-cols-3 gap-2">
            <a href={`${API}/settlements/${id}/pdf?doc=approval`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="rounded-sm gap-1 w-full" data-testid="doc-approval"><FilePdf size={14} /> Approval</Button>
            </a>
            <a href={`${API}/settlements/${id}/pdf?doc=completion`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="rounded-sm gap-1 w-full" data-testid="doc-completion"><FilePdf size={14} /> Completion</Button>
            </a>
            <a href={`${API}/settlements/${id}/pdf?doc=noc`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="rounded-sm gap-1 w-full" data-testid="doc-noc"><FilePdf size={14} /> NOC</Button>
            </a>
          </div>
        </div>

        <DialogFooter className="gap-2">
          {canAct && <>
            <Button size="sm" variant="destructive" className="rounded-sm" onClick={() => act("reject")} data-testid="settlement-reject">Reject</Button>
            <Button size="sm" className="rounded-sm bg-emerald-600 hover:bg-emerald-700" onClick={() => act("approve")} data-testid="settlement-approve">Approve</Button>
          </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  return <div><div className="label-caps">{label}</div><div className="font-semibold mono mt-0.5">{value}</div></div>;
}
